import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { applyEnvToAll, clearEnvFromAll, setEnvIntensityOnAll } from '../lib/envMapApply'
import { splatCacheKey, headOk, loadEquirectAsEnvMap } from '../lib/envCubemapCache'
import { reflectionManifestExists } from '../lib/reflectionCaptureManifest'
import { useLiveTune } from '../hooks/useLiveTune'

/**
 * EnvProbe — loads a pre-baked cubemap for the current splat and applies
 * it as an envMap on the avatar.
 *
 * No in-browser baking. Cubemaps are generated offline by
 * `node aosTools/threejs/bake_ibl.mjs <ply-path>` and committed under
 * `public/assets/environments/cubemaps/<hash>/{px,nx,py,ny,pz,nz}.png`.
 * At runtime this component HEAD-checks the cache dir and, if present,
 * loads + prefilters into a PMREM envMap and assigns it.
 *
 * Cache miss: warns in the console with the bake command to run. The live
 * scene keeps a low ambient fallback so the avatar isn't pitch-black.
 *
 * Enable + envMapIntensity are owned by the liveTuneStore; changes are
 * applied live by mutating the active avatar material's envMapIntensity
 * (resolved via getAvatarMaterial() — character-dependent).
 *
 * Renders nothing.
 */
function waitFor(predicate, { timeout = 30_000, interval = 50 } = {}) {
  return new Promise((resolve, reject) => {
    const start = Date.now()
    const tick = () => {
      if (predicate()) return resolve()
      if (Date.now() - start > timeout) return reject(new Error('waitFor timeout'))
      setTimeout(tick, interval)
    }
    tick()
  })
}

export default function EnvProbe({ splatUrl, onApplied }) {
  const gl    = useThree(s => s.gl)
  const scene = useThree(s => s.scene)
  const tune  = useLiveTune()
  const { iblEnabled, envMapIntensity } = tune.lighting

  // Load / unload the cubemap when splatUrl or iblEnabled changes.
  useEffect(() => {
    if (!iblEnabled) {
      console.log('[EnvProbe] IBL disabled via tune store')
      if (typeof window !== 'undefined') window.__ENV_PROBE__ = { state: 'disabled' }
      onApplied?.(false)
      return
    }

    let cancelled = false
    let appliedTexture = null

    const setState = (state, extra) => {
      if (typeof window === 'undefined') return
      window.__ENV_PROBE__ = { state, ...extra }
    }

    const run = async () => {
      setState('pending')
      try {
        // Phase B: if this splat has multi-capture reflections, ReflectionCaptureVolume
        // owns the specular envMap (position-aware, smoothstep-blended). Stand down so
        // two producers don't fight over material.envMap. This check needs no avatar,
        // so do it before the avatar-ready wait — otherwise a slow avatar load would
        // delay (or, on timeout, mask) the stand-down. Without a manifest, the legacy
        // single-equirect path below stays in charge (real baked data, not a fake fallback).
        if (await reflectionManifestExists(splatUrl)) {
          if (cancelled) return
          console.log('[EnvProbe] reflection captures present — superseded by ReflectionCaptureVolume')
          setState('superseded')
          return
        }
        if (cancelled) return

        // 120s, not the 30s default: a cold avatar boot takes 20-35s+ under
        // software rendering (headless CI, weak hardware) and regularly lost
        // the 30s race — flipping EnvProbe to 'error' and killing IBL for the
        // session even though the avatar arrived moments later.
        await waitFor(() => typeof window !== 'undefined' && window.__AVATAR_READY__ === true, { timeout: 120_000 })
        if (cancelled) return

        const key = await splatCacheKey(splatUrl)
        const cacheUrl = `/assets/environments/equirect/${key}.png`
        setState('checking', { key, cacheUrl })

        const hit = await headOk(cacheUrl)
        if (cancelled) return

        if (!hit) {
          console.warn(
            `[EnvProbe] no equirect IBL found at ${cacheUrl} for splat ${splatUrl}.`,
            `\n  Bake with: npm run bake-ibl -- ${splatUrl}`,
            `\n  Avatar will use the ambient-light fallback until then.`,
          )
          setState('no-cache', { key, cacheUrl })
          return
        }

        const envTexture = await loadEquirectAsEnvMap(gl, cacheUrl)
        if (cancelled) {
          envTexture?.dispose?.()
          return
        }

        scene.environment = envTexture
        // Every material consumes envMap through the standard three pipeline
        // now. Several materials on the rig — apply to every one.
        applyEnvToAll(envTexture, envMapIntensity)
        appliedTexture = envTexture
        console.log(`[EnvProbe] applied equirect IBL from ${cacheUrl}`)
        setState('applied', { key, cacheUrl, envMap: envTexture, intensity: envMapIntensity })
        onApplied?.(true)
      } catch (err) {
        if (cancelled) return
        console.error('[EnvProbe] failed:', err?.message ?? err, err)
        setState('error', { error: err?.message ?? String(err) })
      }
    }

    run()

    return () => {
      cancelled = true
      clearEnvFromAll(appliedTexture)
      if (scene.environment === appliedTexture) {
        scene.environment = null
      }
      appliedTexture?.dispose?.()
      if (typeof window !== 'undefined' && window.__ENV_PROBE__?.envMap === appliedTexture) {
        delete window.__ENV_PROBE__
      }
      onApplied?.(false)
    }
  }, [splatUrl, iblEnabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // Live-mutate envMapIntensity without re-loading the cubemap.
  useEffect(() => {
    setEnvIntensityOnAll(envMapIntensity)
    if (typeof window !== 'undefined' && window.__ENV_PROBE__?.state === 'applied') {
      window.__ENV_PROBE__.intensity = envMapIntensity
    }
  }, [envMapIntensity])

  return null
}
